/**
 * "The questions that stop you installing" — the blocker-clearing section.
 *
 * A solution-aware ServiceNow developer does not need the category explained;
 * they need the six practical objections answered before they will run the
 * install command. ACLs and the roles the service account needs, scoped apps,
 * domain separation, what a hibernating developer instance does, what happens
 * to update sets they already have open, and who promotes to production.
 *
 * Shape follows that job. The answers are NOT hidden behind an accordion:
 * these lines are differentiation, not support content, and a visitor
 * scanning for their own blocker has to be able to see it without clicking.
 * So: a hairline grid, everything visible, two columns on desktop.
 *
 * Copy: every user-visible string comes from the `faq` namespace in
 * locales/{en,nl}/marketing.json (see the key contract) — `eyebrow`,
 * `headline`, `body` and the `items` array of `{ q, a }`. This file authors
 * no English of its own. The one optional key, `faq.moreLabel`, is resolved
 * through `i18n.exists` and falls back to the shipped `footer.resources.faq`
 * label, so a key the copy agent has not filled can never render as a raw
 * key name on the live page.
 *
 * The long-form answers live at /faq (pages/Faq.tsx) and that page is where
 * the link at the bottom goes. It is currently reachable from the footer
 * only; putting it in the top nav is SeracTopNav's call, not this file's.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';

const FAQ_PATH = '/faq';

interface FaqItem {
  q: string;
  a: string;
}

/**
 * i18next hands back the raw key string when a key is missing, so
 * `returnObjects` on an unfilled `faq.items` yields a string, not an array.
 * Coerce defensively and drop any entry that is not a real question/answer
 * pair rather than rendering half a row.
 */
function toFaqItems(raw: unknown): FaqItem[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  return list.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const { q, a } = entry as Record<string, unknown>;
    if (typeof q !== 'string' || q.length === 0) return [];
    if (typeof a !== 'string' || a.length === 0) return [];
    return [{ q, a }];
  });
}

export default function SectionInstallFaq() {
  const { t, i18n } = useTranslation('marketing');
  const isMobile = useIsMobile();

  const items = toFaqItems(t('faq.items', { returnObjects: true }));

  // Label for the link to the long-form FAQ. `faq.moreLabel` is outside the
  // mandatory key set, so resolve it only if it actually exists; otherwise
  // reuse the footer's own FAQ label, which ships in both locales today. If
  // neither is present the link is not rendered at all — a raw key name is
  // never shown.
  const moreLabel = i18n.exists('faq.moreLabel', { ns: 'marketing' })
    ? t('faq.moreLabel')
    : i18n.exists('footer.resources.faq', { ns: 'marketing' })
      ? t('footer.resources.faq')
      : null;

  // Two columns on desktop, one on mobile. Dividers are drawn per cell from
  // its grid position instead of with a wrapper per row, so an odd item
  // count leaves a clean hanging cell rather than a stretched one.
  const columns = isMobile ? 1 : 2;

  return (
    <section
      id="faq"
      aria-labelledby="faq-headline"
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('faq.eyebrow')}
        </div>
        <h2
          id="faq-headline"
          className="t-display"
          style={{
            fontSize: 'clamp(28px, 5vw, 44px)',
            margin: '0 0 12px',
            maxWidth: 700,
            lineHeight: 1.15,
          }}
        >
          {t('faq.headline')}
        </h2>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.6,
            color: 'var(--fg-muted)',
            maxWidth: 640,
            margin: 0,
          }}
        >
          {t('faq.body')}
        </p>

        {items.length > 0 && (
          <div
            style={{
              marginTop: isMobile ? 28 : 40,
              borderTop: '1px solid var(--border)',
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {items.map((item, i) => {
              const row = Math.floor(i / columns);
              const col = i % columns;
              return (
                <div
                  key={i}
                  style={{
                    minWidth: 0,
                    paddingTop: isMobile ? 20 : 26,
                    paddingBottom: isMobile ? 20 : 26,
                    paddingRight: isMobile ? 0 : col === columns - 1 ? 0 : 36,
                    paddingLeft: isMobile ? 0 : col === 0 ? 0 : 36,
                    borderTop: row === 0 ? 'none' : '1px solid var(--border)',
                    borderLeft: col === 0 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: isMobile ? 15.5 : 16,
                      fontWeight: 500,
                      lineHeight: 1.4,
                      letterSpacing: '-0.005em',
                      color: 'var(--fg)',
                    }}
                  >
                    {item.q}
                  </h3>
                  <p
                    style={{
                      margin: '10px 0 0',
                      fontSize: isMobile ? 14 : 14.5,
                      lineHeight: 1.65,
                      color: 'var(--fg-muted)',
                    }}
                  >
                    {item.a}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* The long tail lives on /faq. Accent is reserved for things the
            visitor can act on, so the link carries it and nothing above
            it does. */}
        {moreLabel !== null && (
          <div
            style={{
              marginTop: isMobile ? 24 : 32,
              paddingTop: isMobile ? 20 : 24,
              borderTop: '1px solid var(--border)',
            }}
          >
            <Link
              to={FAQ_PATH}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                letterSpacing: '0.02em',
                color: 'var(--ice-400)',
                textDecoration: 'none',
                transition: 'color 150ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--ice-300)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--ice-400)';
              }}
              onFocus={(e) => {
                e.currentTarget.style.color = 'var(--ice-300)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.color = 'var(--ice-400)';
              }}
            >
              <span>{moreLabel}</span>
              <Icon.arrow s={13} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
